/**
 * Verjaardag-keuze — pure utility-functies.
 *
 * Spec §3 (Kernlogica — verjaardag bepalen):
 *   - berekenVerjaardagDatum: bepaalt voor een gegeven jaar de verjaardagdatum
 *     van een persoon (inclusief schrikkeljaar-correctie).
 *   - isVerjaardagWerkdag: controleert of de verjaardagdatum op een werkdag
 *     valt (niet weekend, niet bestaande feestdag).
 *
 * Geen DB-aanroepen, geen side-effects — puur deterministisch.
 */

import { isFeestdag, type PublicHolidayInfo } from './leave-calculations'

/**
 * Bepaalt de verjaardagdatum voor een specifiek jaar.
 *
 * Edge case (spec §13.1): geboren op 29 februari → verjaardag op 28 februari
 * in niet-schrikkeljaren (Nederlandse conventie — gelijk aan BSN/BRP-praktijk).
 *
 * Schrikkeljaar-regel: deelbaar door 4, behalve eeuwen (deelbaar door 100)
 * tenzij ook deelbaar door 400.
 */
export function berekenVerjaardagDatum(birthDate: Date, jaar: number): Date {
  const maand = birthDate.getUTCMonth() // 0-indexed
  let dag = birthDate.getUTCDate()

  // Schrikkeldag-correctie: 29 februari → 28 februari in niet-schrikkeljaren
  if (maand === 1 && dag === 29) {
    const isSchrikkelJaar = (jaar % 4 === 0 && jaar % 100 !== 0) || jaar % 400 === 0
    if (!isSchrikkelJaar) dag = 28
  }

  // UTC-datum zonder tijdcomponent — vergelijkbaar met ander datum-gebruik
  // in @streep/utils (sameDay, calculateWerkdagen).
  return new Date(Date.UTC(jaar, maand, dag))
}

/**
 * Controleert of de verjaardagdatum op een werkdag valt.
 *
 * Geen keuze nodig als:
 *   - weekend (zondag 0, zaterdag 6)
 *   - bestaande verplichte feestdag (isFeestdag uit leave-calculations.ts —
 *     Goede Vrijdag (isVerplicht=false) telt NIET als feestdag, dus daar is
 *     wèl een keuze nodig — zie spec §13.1).
 */
export function isVerjaardagWerkdag(
  verjaardagDatum: Date,
  feestdagen: PublicHolidayInfo[],
): boolean {
  const dag = verjaardagDatum.getUTCDay()
  if (dag === 0 || dag === 6) return false
  if (isFeestdag(verjaardagDatum, feestdagen)) return false
  return true
}

/**
 * Berekent de leeftijd die iemand op een gegeven datum wordt. Hier "wordt X
 * jaar" — de datum zelf telt als bereikt-leeftijd. Spec §8.2.
 */
export function leeftijdOpVerjaardag(birthDate: Date, verjaardagDatum: Date): number {
  return verjaardagDatum.getUTCFullYear() - birthDate.getUTCFullYear()
}

/**
 * Batch-48 — DE niet-blokkerende waarschuwingszin bij inplannen op een vrij
 * gekozen verjaardag (spec 95 §3.2, letterlijk).
 *
 * Gedeeld tussen server en client: de API zet 'm in `afwezigheidWaarschuwingen`
 * en in het `VERJAARDAG_VRIJ`-conflict, het planbord stelt er vooraf dezelfde
 * vraag mee (FO-340-§10.3-patroon). Eén bron, zodat het bord vooraf exact vraagt
 * wat de server achteraf meldt - en de dubbele-melding-onderdrukking op het bord
 * blijft werken als de tekst ooit wijzigt.
 *
 * Blokkeert nooit: de planner mag méér weten dan het systeem (de medewerker kan
 * alsnog willen werken) en zijn "ja" is leidend.
 */
export function verjaardagVrijWaarschuwingTekst(naam: string): string {
  return `${naam} is die dag jarig en heeft vrij gekozen - toch inplannen?`
}
